import axios from 'axios';
import { getAuthCookies } from '@webmotors/consumer-shared-auth';
import { ENV } from '@/app/config';

type TPhotoItem = {
	code: number | string;
	filename: string;
	index: number;
	url: string;
};

type TSingleUploadResponse = {
	success: boolean;
	code: string | number;
	filename: string;
	url: string;
};

const uploadClient = axios.create({
	timeout: 60_000,
});

uploadClient.interceptors.request.use((config) => {
	const { idToken } = getAuthCookies();
	if (idToken) {
		config.headers.Authorization = `Bearer ${idToken}`;
	}
	return config;
});

const getUploadUrl = (action: string): string =>
	`${ENV.API_UPLOAD_URL}/${action}`;

const photoService = {
	/**
	 * POST /GetPictures
	 * Retorna lista de fotos associadas ao hash do anúncio
	 */
	getPictures: async (photoHash: string): Promise<TPhotoItem[]> => {
		const formData = new FormData();
		formData.append('hashAnuncio', photoHash);

		const { data } = await uploadClient.post<TPhotoItem[]>(
			getUploadUrl('GetPictures'),
			formData
		);
		return data ?? [];
	},

	/**
	 * POST /MultipleUpload
	 * Upload de um arquivo por vez — mesmo comportamento do Vender.
	 * Retorna objeto único { success, code, filename, url }.
	 */
	uploadPhoto: async (photoHash: string, file: File): Promise<TSingleUploadResponse> => {
		const formData = new FormData();
		formData.append('images', file);
		formData.append('hashAnuncio', photoHash);

		const { data } = await uploadClient.post<TSingleUploadResponse>(
			getUploadUrl('MultipleUpload'),
			formData
		);
		return data;
	},

	/**
	 * POST /RemovePicture
	 * Remove uma foto pelo código — envia JSON body (igual ao Vender).
	 */
	removePicture: async (photoHash: string, imageCode: string | number, imageName: string): Promise<void> => {
		await uploadClient.post(getUploadUrl('RemovePicture'), {
			imageCode: String(imageCode),
			imageName,
			hashAnuncio: photoHash
		});
	},

	/**
	 * POST /UpdateImageOrder
	 * Atualiza a ordem das fotos
	 */
	updateImageOrder: async (photoHash: string, orderedCodes: (string | number)[]): Promise<void> => {
		const formData = new FormData();
		formData.append('hashAnuncio', photoHash);
		orderedCodes.forEach((code, index) =>
			formData.append(`code${index + 1}`, String(code))
		);

		await uploadClient.post(getUploadUrl('UpdateImageOrder'), formData);
	}
};

export { photoService };
export type { TPhotoItem, TSingleUploadResponse };
